import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { useT as useTranslation } from "@/i18n";

const DISMISSED_KEY = "lumioos_pwa_install_dismissed_at";
const DISMISS_DAYS = 7;

export default function PWAInstallButton({ variant = "floating" }) {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showFloating, setShowFloating] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(ios);

    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }
    const dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    const dismissedRecently = dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400000;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (variant === "floating" && !dismissedRecently) {
        setTimeout(() => setShowFloating(true), 4000);
      }
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    if (variant === "floating" && ios && !dismissedRecently) {
      setTimeout(() => setShowFloating(true), 4000);
    }
    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, [variant]);

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
        setShowFloating(false);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: show context-aware install guide (iOS modal OR generic)
      setShowIOSHelp(true);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setShowFloating(false);
  };

  if (isInstalled) return null;

  // Inline button (for hero/header — ALWAYS renders so users see the install CTA
  // from the first visit. Click behavior is context-aware: native prompt if available,
  // iOS modal if iPhone/iPad, generic browser-specific modal otherwise.)
  if (variant === "inline") {
    return (
      <>
        <button onClick={install} data-testid="pwa-install-inline"
          className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-md transition-colors">
          <Download className="w-4 h-4" />
          {t("pwa_install_lumioos")}
        </button>
        {showIOSHelp && <IOSHelpModal onClose={() => setShowIOSHelp(false)} isIOS={isIOS} t={t} />}
      </>
    );
  }

  // Floating bottom-right banner
  if (!showFloating || (!deferredPrompt && !isIOS)) return null;
  return (
    <>
      <div className="fixed bottom-5 left-5 z-40 max-w-sm" data-testid="pwa-install-floating">
        <div className="bg-white border-2 border-orange-200 shadow-2xl shadow-orange-100 rounded-xl p-3 pr-2">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 flex items-center justify-center shrink-0">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zinc-900">{t("pwa_install_lumioos")}</div>
              <div className="text-xs text-zinc-600 mt-0.5">{t("pwa_install_subtitle")}</div>
              <div className="mt-2 flex items-center gap-2">
                <button onClick={install} data-testid="pwa-install-confirm"
                  className="btn-accent text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded">
                  {t("pwa_install_short")}
                </button>
                <button onClick={dismiss} className="text-xs text-zinc-500 hover:text-zinc-800 font-semibold">
                  {t("pwa_not_now")}
                </button>
              </div>
            </div>
            <button onClick={dismiss} className="p-1 hover:bg-zinc-100 rounded text-zinc-400" aria-label="Dismiss">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
      {showIOSHelp && <IOSHelpModal onClose={() => setShowIOSHelp(false)} isIOS={isIOS} t={t} />}
    </>
  );
}

function IOSHelpModal({ onClose, isIOS, t }) {
  // Detect platform once for tailored instructions
  const ua = typeof window !== "undefined" ? window.navigator.userAgent : "";
  const isAndroid = /Android/i.test(ua);
  const isMacChrome = /Mac/.test(ua) && /Chrome/.test(ua) && !/Safari\/[^C]+$/.test(ua);
  // iOS PWA install ONLY works in Safari. If the user opens the site in
  // Chrome / Firefox / Instagram in-app browser on iPhone, "Add to Home
  // Screen" will either be missing or install a broken bookmark. We detect
  // this and prompt them to switch to Safari before showing the steps.
  const iosNonSafari =
    isIOS && (/CriOS|FxiOS|EdgiOS|GSA|FBAN|FBAV|Instagram|Line/i.test(ua) || !/Safari/i.test(ua));

  let title = t("pwa_install_lumioos");
  if (isIOS) title = t("pwa_modal_title_ios");
  else if (isAndroid) title = t("pwa_modal_title_android");
  else if (isMacChrome) title = t("pwa_modal_title_mac");
  else title = t("pwa_modal_title_desktop");

  const stepsKey = isIOS ? "ios" : isAndroid ? "android" : isMacChrome ? "mac" : "desktop";
  const steps = [1, 2, 3, 4].map((i) => t(`pwa_modal_${stepsKey}_step${i}`));

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch { /* clipboard blocked — user can long-press the URL instead */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="pwa-install-modal">
        <h3 className="text-xl font-bold tracking-tight">{title}</h3>
        {iosNonSafari && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border-2 border-amber-300" data-testid="pwa-ios-safari-warning">
            <div className="text-sm font-bold text-amber-900">⚠ {t("pwa_ios_safari_required")}</div>
            <div className="mt-1 text-xs text-amber-800 leading-relaxed">
              {t("pwa_ios_safari_required_sub")}
            </div>
            <button
              onClick={copyUrl}
              data-testid="pwa-ios-copy-link"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold bg-amber-900 text-white px-3 py-1.5 rounded hover:bg-amber-950"
            >
              {t("pwa_ios_copy_link")}
            </button>
          </div>
        )}
        <ol className="mt-4 space-y-3 text-sm text-zinc-700">
          {steps.map((step, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-bold text-orange-600">{i + 1}.</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
        <button onClick={onClose} className="mt-5 w-full btn-accent text-sm font-bold px-4 py-2.5 rounded-md">{t("pwa_got_it")}</button>
      </div>
    </div>
  );
}
